// src/pages/SuperAdmin/CountryAdmins/CountryAdminsPage.jsx
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Pencil, Plus } from "lucide-react";

import TableToolbar from "../../../components/table/TableToolbar";
import Paginator from "../../../components/table/Paginator";
import useTableController from "../../../components/table/useTableController";

import ReusableModal from "../../../components/common/ReusableModal";
import FormButton from "../../../components/form/FormButton";
import FormField from "../../../components/form/FormField";
import FormInput from "../../../components/form/FormInput";
import FormSelect from "../../../components/form/FormSelect";

import {
  listCountryAdmins,
  createCountryAdmin,
  toggleCountryAdminStatus,
  updateCountryAdminSupport,
} from "./countryAdminApi";

import { countries } from "../Venues/locationOptions";
import "./country-admins.css";

const emptyForm = {
  name: "",
  email: "",
  password: "",
  country: "",
  supportPhoneE164: "",
};

const emptySupportForm = {
  email: "",
  supportPhoneE164: "",
};

const CountryAdminsPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [supportForm, setSupportForm] = useState(emptySupportForm);
  const [supportErrors, setSupportErrors] = useState({});

  const tc = useTableController(items, {
    searchKeys: ["name", "email", "supportPhoneE164", "country"],
    initialPageSize: 10,
  });

  const load = async () => {
    setLoading(true);

    try {
      const data = await listCountryAdmins({ q: tc.searchTerm });
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error("Failed to load country admins");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setForm(emptyForm);
    setErrors({});
    setCreating(true);
  };

  const closeCreate = () => {
    if (busyId === "create") return;
    setCreating(false);
    setForm(emptyForm);
    setErrors({});
  };

  const set = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));

    setErrors((prev) => {
      if (!prev[key]) return prev;

      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const validate = () => {
    const e = {};

    if (!form.name.trim()) {
      e.name = "Name is required";
    }

    if (!form.email.trim()) {
      e.email = "Email is required";
    }

    if (!form.supportPhoneE164.trim()) {
      e.supportPhoneE164 = "Support phone is required";
    } else if (!/^\+[1-9]\d{7,14}$/.test(form.supportPhoneE164.trim())) {
      e.supportPhoneE164 = "Use E.164 format, for example +919876543210";
    }

    if (!form.password.trim()) {
      e.password = "Password is required";
    }

    if (!form.country.trim()) {
      e.country = "Country is required";
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleCreate = async (e) => {
    e.preventDefault();

    if (busyId === "create") return;

    if (!validate()) {
      toast.error("All fields are required");
      return;
    }

    try {
      setBusyId("create");

      await createCountryAdmin({
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        country: form.country.trim(),
        supportPhoneE164: form.supportPhoneE164.trim(),
      });

      toast.success("Country admin created");
      setCreating(false);
      setForm(emptyForm);
      setErrors({});
      await load();
    } catch (err) {
      const msg = err?.response?.data?.message || "Create failed";
      toast.error(msg);
    } finally {
      setBusyId(null);
    }
  };

  const openEditSupport = (admin) => {
    setEditingAdmin(admin);
    setSupportForm({
      email: String(admin?.email || ""),
      supportPhoneE164: String(admin?.supportPhoneE164 || ""),
    });
    setSupportErrors({});
  };

  const closeEditSupport = () => {
    const id = editingAdmin?._id || editingAdmin?.id;
    if (id && busyId === `support:${id}`) return;

    setEditingAdmin(null);
    setSupportForm(emptySupportForm);
    setSupportErrors({});
  };

  const setSupport = (key, value) => {
    setSupportForm((prev) => ({ ...prev, [key]: value }));
    setSupportErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleUpdateSupport = async (event) => {
    event.preventDefault();

    const id = editingAdmin?._id || editingAdmin?.id;
    if (!id || busyId === `support:${id}`) return;

    const nextErrors = {};
    const email = supportForm.email.trim().toLowerCase();
    const supportPhoneE164 = supportForm.supportPhoneE164.trim();

    if (!email) nextErrors.email = "Support email is required";
    if (!supportPhoneE164) {
      nextErrors.supportPhoneE164 = "Support phone is required";
    } else if (!/^\+[1-9]\d{7,14}$/.test(supportPhoneE164)) {
      nextErrors.supportPhoneE164 =
        "Use E.164 format, for example +919876543210";
    }

    if (Object.keys(nextErrors).length > 0) {
      setSupportErrors(nextErrors);
      return;
    }

    try {
      setBusyId(`support:${id}`);
      await updateCountryAdminSupport(id, { email, supportPhoneE164 });
      toast.success("Country support contact updated");
      setEditingAdmin(null);
      setSupportForm(emptySupportForm);
      setSupportErrors({});
      await load();
    } catch (err) {
      const msg = err?.response?.data?.message || "Support update failed";
      toast.error(msg);
    } finally {
      setBusyId(null);
    }
  };

  const handleToggleActive = async (admin) => {
    const id = admin._id || admin.id;
    if (!id) return;

    const nextActive = !admin.active;

    try {
      setBusyId(id);
      await toggleCountryAdminStatus(id, nextActive);
      toast.success(`Country admin ${nextActive ? "activated" : "deactivated"}`);
      await load();
    } catch (err) {
      const msg = err?.response?.data?.message || "Update failed";
      toast.error(msg);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="country-admins-page">
      <div className="sa-head-row country-admins-head">
        <h1 className="sa-h1 sa-topbar-managed-page-title">Country Admins</h1>

        <FormButton
          type="button"
          variant="primary"
          className="country-admins-new-btn"
          onClick={openCreate}
        >
          <Plus className="lucide" /> New Country Admin
        </FormButton>
      </div>

      <TableToolbar
        searchTerm={tc.searchTerm}
        onSearchChange={tc.setSearch}
        searchPlaceholder="Search by name, email, or country..."
      />

      <div className="sa-card country-admins-card">
        {loading ? (
          <div className="sa-placeholder" style={{ height: 120 }}>
            Loading…
          </div>
        ) : tc.total === 0 ? (
          <div className="sa-empty">
            <div>No country admins found.</div>

            <FormButton type="button" variant="primary" onClick={openCreate}>
              Create your first country admin
            </FormButton>
          </div>
        ) : (
          <div className="country-admins-table-zone">
            <div className="sa-table-wrap country-admins-table-wrap">
              <table className="sa-table country-admins-table">
                <thead>
                  <tr>
                    <th style={{ width: 60 }}>S.N.</th>
                    <th>Name</th>
                    <th>Support Email</th>
                    <th>Support Phone</th>
                    <th style={{ width: 100 }}>Country</th>
                    <th style={{ width: 120 }}>Status</th>
                    <th style={{ width: 90, textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {tc.rows.map((a, i) => {
                    const id = a._id || a.id;

                    return (
                      <tr key={id || a.email}>
                        <td>{(tc.page - 1) * tc.pageSize + i + 1}</td>
                        <td>{a.name || "—"}</td>
                        <td>{a.email || "—"}</td>
                        <td>{a.supportPhoneE164 || "—"}</td>
                        <td>{a.country || "—"}</td>
                        <td>
                          <span className={`sa-status ${a.active ? "active" : "inactive"}`}>
                            {a.active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="sa-actions-cell">
                          <div className="country-admins-actions">
                            <button
                              type="button"
                              className="country-admins-edit-btn"
                              title="Edit support contact"
                              aria-label="Edit support contact"
                              onClick={() => openEditSupport(a)}
                              disabled={busyId === id || busyId === `support:${id}`}
                            >
                              <Pencil size={16} aria-hidden="true" />
                            </button>

                            <label
                              className="sa-switch"
                              title={a.active ? "Deactivate" : "Activate"}
                              aria-label={a.active ? "Deactivate" : "Activate"}
                            >
                              <input
                                type="checkbox"
                                checked={!!a.active}
                                onChange={() => handleToggleActive(a)}
                                disabled={busyId === id || busyId === `support:${id}`}
                              />
                              <span />
                            </label>
                          </div>
                        </td>

                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <Paginator
              page={tc.page}
              totalPages={tc.totalPages}
              onPage={tc.setPage}
              pageSize={tc.pageSize}
              onPageSizeChange={tc.setPageSize}
              pageSizeOptions={[5, 10, 20, 50]}
            />
          </div>
        )}
      </div>

      <ReusableModal
        open={creating}
        title="Create Country Admin"
        width="640px"
        onClose={closeCreate}
        className="country-admins-modal"
        closeOnBackdrop={false}
      >
        <form className="country-admins-form" onSubmit={handleCreate} noValidate>
          <div className="country-admins-form-grid">
            <FormField label="Name" error={errors.name}>
              <FormInput
                type="text"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                placeholder="India Manager"
                required
              />
            </FormField>

            <FormField label="Support Email" error={errors.email}>
              <FormInput
                type="email"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                placeholder="country.admin@example.com"
                required
              />
            </FormField>

            <FormField label="Support Phone" error={errors.supportPhoneE164}>
              <FormInput
                type="tel"
                value={form.supportPhoneE164}
                onChange={(e) => set("supportPhoneE164", e.target.value)}
                placeholder="+919876543210"
                required
              />
            </FormField>

            <FormField label="Password" error={errors.password}>
              <FormInput
                type="password"
                value={form.password}
                onChange={(e) => set("password", e.target.value)}
                placeholder="••••••••"
                required
              />
            </FormField>

            <FormField label="Country" error={errors.country}>
              <FormSelect
                value={form.country}
                onChange={(e) => set("country", e.target.value)}
                required
              >
                <option value="">Choose country…</option>
                {countries.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name} ({c.code})
                  </option>
                ))}
              </FormSelect>
            </FormField>
          </div>
          <div className="country-admins-form-actions">
            <FormButton type="submit" variant="primary" disabled={busyId === "create"}>
              {busyId === "create" ? "Creating..." : "Create"}
            </FormButton>

            <FormButton
              type="button"
              variant="secondary"
              onClick={closeCreate}
              disabled={busyId === "create"}
            >
              Cancel
            </FormButton>
          </div>


        </form>
      </ReusableModal>

      <ReusableModal
        open={!!editingAdmin}
        title="Edit Country Support Contact"
        width="640px"
        onClose={closeEditSupport}
        className="country-admins-modal"
        closeOnBackdrop={false}
      >
        <form
          className="country-admins-form"
          onSubmit={handleUpdateSupport}
          noValidate
        >
          <div className="country-admins-form-grid">
            <FormField label="Support Email" error={supportErrors.email}>
              <FormInput
                type="email"
                value={supportForm.email}
                onChange={(e) => setSupport("email", e.target.value)}
                placeholder="country.admin@example.com"
                required
              />
            </FormField>

            <FormField
              label="Support Phone"
              error={supportErrors.supportPhoneE164}
            >
              <FormInput
                type="tel"
                value={supportForm.supportPhoneE164}
                onChange={(e) =>
                  setSupport("supportPhoneE164", e.target.value)
                }
                placeholder="+919876543210"
                required
              />
            </FormField>

            <FormField label="Country">
              <FormSelect value={editingAdmin?.country || ""} disabled>
                {countries.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.name} ({country.code})
                  </option>
                ))}
              </FormSelect>
            </FormField>
          </div>

          <div className="country-admins-form-actions">
            <FormButton
              type="submit"
              variant="primary"
              disabled={
                busyId ===
                `support:${editingAdmin?._id || editingAdmin?.id}`
              }
            >
              {busyId ===
              `support:${editingAdmin?._id || editingAdmin?.id}`
                ? "Saving..."
                : "Save"}
            </FormButton>

            <FormButton
              type="button"
              variant="secondary"
              onClick={closeEditSupport}
              disabled={
                busyId ===
                `support:${editingAdmin?._id || editingAdmin?.id}`
              }
            >
              Cancel
            </FormButton>
          </div>
        </form>
      </ReusableModal>
    </div>
  );
};

export default CountryAdminsPage;